import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { useState } from "react";
import InputPasswordWithToggle from "../component/InputPasswordWithToggle";
import { CertificateApiResponse } from "../modals/ModalDigitalCertificate";

type FormValues = {
  file: File | null;
  password_certificate: string;
};

type FormProps = {
  onSubmit: (values: {
    partnersId: string;
    certificate: string;
    password_certificate: string;
  }) => Promise<{ ok: boolean; data?: unknown; errors?: unknown[] }>;
  partnersId: string;
};

const validationSchema = Yup.object({
  file: Yup.mixed().required("Arquivo do certificado é obrigatório."),
  password_certificate: Yup.string().required(
    "Senha do certificado é obrigatória."
  ),
});

function FormDigitalCertificate(props: FormProps) {
  const { notifySuccess, notifyError } = useToastContext();
  const [fileError, setFileError] = useState<string | null>(null);

  const formik = useFormik<FormValues>({
    initialValues: {
      file: null,
      password_certificate: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!values.file) {
        setFileError("O arquivo do certificado é obrigatório.");
        return;
      }

      setFileError(null);

      try {
        const fileBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(values.file!);
          reader.onload = () =>
            resolve((reader.result as string).split(",")[1]);
          reader.onerror = (error) => reject(error);
        });

        const passwordPlainText = values.password_certificate;

        const result = await props.onSubmit({
          partnersId: props.partnersId,
          certificate: fileBase64,
          password_certificate: passwordPlainText,
        });

        if (result.ok && !(result as CertificateApiResponse).code) {
          notifySuccess("Certificado digital salvo com sucesso!");
        } else if (!result.ok && !(result as CertificateApiResponse).code) {
          notifyError(
            "Erro ao salvar certificado: " +
              (result.errors?.[0] || "Erro desconhecido")
          );
        }
        return result;
      } catch (err) {
        console.error(err);
        notifyError("Erro durante o processamento do certificado/senha.");
        return { ok: false, errors: [{ message: "Erro de processamento" }] };
      }
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    formik.setFieldValue("file", file);
    setFileError(null);
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold mb-4">
            Upload de Certificado Digital
          </h2>
          <div className="grid grid-cols-1 gap-4 mb-4">
            <div className="flex flex-col">
              <label
                htmlFor="file"
                className="text-sm font-medium text-gray-700 required-label"
              >
                Arquivo do Certificado (.pfx, .p12):
              </label>
              <input
                id="file"
                name="file"
                type="file"
                accept=".pfx,.p12"
                onChange={handleFileChange}
                className="mt-1 block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-red-50 file:text-[#E44131]
                  hover:file:bg-red-100"
              />
              {fileError && (
                <div className="text-red-500 text-xs mt-1">{fileError}</div>
              )}
              {formik.touched.file && formik.errors.file && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.file as string}
                </div>
              )}
            </div>

            <InputPasswordWithToggle
              label="Senha do Certificado:"
              name="password_certificate"
              required={true}
            />
          </div>
        </section>

        <div className="w-full flex justify-end mt-6">
          <ButtonWithLoader
            isLoading={formik.isSubmitting}
            disabled={!formik.isValid || !formik.values.file}
            onClick={formik.handleSubmit}
          >
            Salvar Certificado
          </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  );
}

export default FormDigitalCertificate;
